// Design deliverables catalog + orchestration. The Design tab treats the DDD / NLU / Customer
// worksheets as persisted, versioned documents (S3 artifacts), not one-shot downloads. Each is built
// in-browser (reusing the existing builders) then uploaded via POST /projects/{id}/artifacts.
//
// `regenerateDeliverables` (re)builds the applicable set and uploads them sequentially — threading the
// project's optimistic-concurrency `expectedVersion` because each upload bumps it (same discipline as
// the source-upload loop). It runs automatically when a Refine completes, and on the per-document
// Refresh/Generate action.

import { buildDddArrayBuffer } from './dddWorksheet';
import { buildNluArrayBuffer } from './nluWorksheet';
import { buildCustomerArrayBuffer } from './customerWorksheet';
import { decideLexStrategy, LEX_STRATEGY } from './lexStrategy';
import { uploadArtifact } from '../api_scripts/solnStudioClient';

export const DESIGN_ARTIFACT_TYPES = ['DDD', 'NLU_WORKSHEET', 'CUSTOMER_WORKSHEET'];

function safeName(model, projectName) {
  return (projectName || model?.meta?.customer || 'Project').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '');
}

// The deliverable catalog. `build(model)` returns an ArrayBuffer (or { buffer }); `appliesTo(model)`
// gates conditional documents (the standalone NLU worksheet only for a large NLU bot).
export const DELIVERABLES = [
  {
    id: 'ddd',
    type: 'DDD',
    label: 'DDD Worksheet',
    description: 'AWS Connect Export-format workbook — core Connect config (+ Lex tabs when a bot is in scope).',
    filename: (model, projectName) => `${safeName(model, projectName)}_DDD.xlsx`,
    build: async (model) => (await buildDddArrayBuffer(model)).buffer,
  },
  {
    id: 'customer-worksheet',
    type: 'CUSTOMER_WORKSHEET',
    label: 'Customer Worksheet',
    description: 'Fill-in workbook for the customer: open questions, integration data needed, new use cases.',
    filename: (model, projectName) => `${safeName(model, projectName)}_Customer_Worksheet.xlsx`,
    build: (model) => buildCustomerArrayBuffer(model),
  },
  {
    id: 'nlu-worksheet',
    type: 'NLU_WORKSHEET',
    label: 'NLU Design Worksheet',
    description: 'Standalone NLU design doc (bot config, intents × languages, slots, slot types, routing).',
    appliesTo: (model) => decideLexStrategy(model).strategy === LEX_STRATEGY.STANDALONE_NLU,
    filename: (model, projectName) => `${safeName(model, projectName)}_NLU_Design.xlsx`,
    build: (model) => buildNluArrayBuffer(model),
  },
];

/** Which deliverables apply to the current model (the standalone NLU worksheet is conditional). */
export function applicableDeliverables(model) {
  return DELIVERABLES.filter((d) => !d.appliesTo || d.appliesTo(model || {}));
}

function arrayBufferToBase64(ab) {
  const bytes = new Uint8Array(ab);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/**
 * (Re)build + upload deliverables, threading the project version through sequential guarded writes.
 * @param {object} args
 * @param {string} args.projectId
 * @param {object} args.model              the (fresh) Design Model
 * @param {number} args.designModelVersion the design-model version these docs reflect
 * @param {number} args.expectedVersion    the current project.version (bumped by each upload)
 * @param {function} args.getApiToken
 * @param {string[]} [args.only]           limit to these deliverable ids (default: all applicable)
 * @returns {Promise<{ lastVersion: number, uploaded: string[] }>}
 */
export async function regenerateDeliverables({ projectId, model, designModelVersion, expectedVersion, getApiToken, only }) {
  let version = expectedVersion;
  const uploaded = [];
  const set = applicableDeliverables(model).filter((d) => !only || only.includes(d.id));
  for (const d of set) {
    const buffer = await d.build(model);
    const contentBase64 = arrayBufferToBase64(buffer);
    const { project } = await uploadArtifact(
      projectId,
      version,
      { id: d.id, type: d.type, filename: d.filename(model), version: designModelVersion, contentBase64 },
      getApiToken,
    );
    version = project.version;
    uploaded.push(d.id);
  }
  return { lastVersion: version, uploaded };
}
